import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, Type, Star, X } from 'lucide-react';
import GoogleFontsService, { GoogleFont } from '../../services/googleFontsService';

interface FontSelectorProps {
  selectedFont: string;
  onFontSelect: (fontFamily: string) => void;
  onClose?: () => void;
  className?: string;
}

const FONT_CATEGORIES = [
  { id: 'all', label: 'All Fonts', icon: Type },
  { id: 'popular', label: 'Popular', icon: Star },
  { id: 'sans-serif', label: 'Sans Serif', icon: Type },
  { id: 'serif', label: 'Serif', icon: Type },
  { id: 'display', label: 'Display', icon: Type },
  { id: 'handwriting', label: 'Handwriting', icon: Type },
  { id: 'monospace', label: 'Monospace', icon: Type },
] as const;

export const FontSelector: React.FC<FontSelectorProps> = ({
  selectedFont,
  onFontSelect,
  onClose,
  className = ''
}) => {
  const [fonts, setFonts] = useState<GoogleFont[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('popular');
  const [loading, setLoading] = useState(true);
  const [loadingFonts, setLoadingFonts] = useState<Set<string>>(new Set());

  const googleFontsService = useMemo(() => GoogleFontsService.getInstance(), []);

  // Load fonts on component mount
  useEffect(() => {
    const loadFonts = async () => {
      setLoading(true);
      try {
        // Initialize with popular fonts (no API key needed)
        const loadedFonts = await googleFontsService.initializeWithPopularFonts();
        setFonts(loadedFonts);
        
        // Preload some popular fonts in the background
        googleFontsService.preloadPopularFonts();
      } catch (error) {
        console.error('Error loading fonts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFonts();
  }, [googleFontsService]);

  // Filter fonts based on search and category
  const filteredFonts = useMemo(() => {
    let filtered = fonts;

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = googleFontsService.searchFonts(searchQuery);
    }

    // Apply category filter
    if (selectedCategory === 'popular') {
      const popularFonts = [
        'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Oswald', 'Source Sans Pro',
        'Raleway', 'PT Sans', 'Lora', 'Nunito', 'Ubuntu', 'Playfair Display', 'Merriweather',
        'Poppins', 'Noto Sans', 'Fira Sans', 'Work Sans', 'Arial', 'Helvetica', 'Times New Roman', 'Georgia'
      ];
      filtered = filtered.filter(font => popularFonts.includes(font.family));
    } else if (selectedCategory !== 'all') {
      filtered = filtered.filter(font => font.category === selectedCategory);
    }

    return filtered.slice(0, 50); // Limit to 50 fonts for performance
  }, [fonts, searchQuery, selectedCategory, googleFontsService]);

  // Handle font selection
  const handleFontSelect = useCallback(async (fontFamily: string) => {
    // Add to loading state
    setLoadingFonts(prev => new Set(prev).add(fontFamily));

    try {
      // Load the font if it's not a system font
      await googleFontsService.loadFont(fontFamily, ['400', '700']);
      
      // Apply the font
      onFontSelect(fontFamily);
    } catch (error) {
      console.error('Error loading font:', error);
    } finally {
      // Remove from loading state
      setLoadingFonts(prev => {
        const newSet = new Set(prev);
        newSet.delete(fontFamily);
        return newSet;
      });
    }
  }, [googleFontsService, onFontSelect]);

  // Get font preview style
  const getFontPreviewStyle = useCallback((fontFamily: string) => {
    return {
      fontFamily: googleFontsService.getFontFamilyCSS(fontFamily),
      fontSize: '16px',
      lineHeight: '1.4'
    };
  }, [googleFontsService]);

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-xl border border-gray-200 ${className}`}>
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading fonts...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-xl border border-gray-200 ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <Type className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Choose Font</h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        )}
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search fonts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-wrap gap-2">
          {FONT_CATEGORIES.map((category) => (
            <button
              key={category.id}
              onClick={(e) => { e.stopPropagation(); setSelectedCategory(category.id); }}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                selectedCategory === category.id
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Font List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredFonts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Type className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No fonts found matching your criteria.</p>
          </div>
        ) : (
          <div className="p-2">
            {filteredFonts.map((font) => (
              <button
                key={font.family}
                onClick={(e) => { e.stopPropagation(); handleFontSelect(font.family); }}
                disabled={loadingFonts.has(font.family)}
                className={`w-full p-3 text-left rounded-lg transition-colors hover:bg-gray-50 ${
                  selectedFont === font.family
                    ? 'bg-blue-50 border border-blue-200'
                    : 'border border-transparent'
                } ${
                  loadingFonts.has(font.family) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div 
                      className="font-medium text-gray-900 truncate mb-1"
                      style={getFontPreviewStyle(font.family)}
                    >
                      {font.family}
                    </div>
                    <div 
                      className="text-sm text-gray-600 truncate"
                      style={getFontPreviewStyle(font.family)}
                    >
                      The quick brown fox jumps over the lazy dog
                    </div>
                  </div>
                  <div className="ml-3 flex-shrink-0">
                    <span className="text-xs text-gray-400 capitalize">
                      {font.category}
                    </span>
                    {loadingFonts.has(font.family) && (
                      <div className="ml-2 inline-block">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <p className="text-xs text-gray-500 text-center">
          Fonts provided by Google Fonts and system fonts
        </p>
      </div>
    </div>
  );
};
